// src/notifications/errorBus.js
//
// A tiny, framework-agnostic notification store. It lives outside React so that
// non-React code — the axios interceptor, window.onerror, unhandledrejection —
// can surface errors too. The React layer subscribes via useSyncExternalStore
// (see context/NotificationContext.jsx).

import { normalizeError } from '../utils/normalizeError';

const MAX_NOTIFICATIONS = 50;
// Identical notifications arriving within this window are collapsed into one
// (with a count) so a failing loop or retry storm can't spam the screen.
const DEDUPE_WINDOW_MS = 4000;

let notifications = []; // newest first; replaced (never mutated) on every change
const listeners = new Set();
let seq = 0;

function nextId() {
    seq += 1;
    return `qd-notif-${Date.now().toString(36)}-${seq}`;
}

function emit() {
    for (const listener of listeners) listener();
}

// ── useSyncExternalStore plumbing ───────────────────────────────────────────
export function subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function getSnapshot() {
    return notifications;
}

function signatureOf(n) {
    return [n.type, n.title, n.message, n.status, n.url].join('|');
}

/**
 * Adds a fully-formed notification and returns its id.
 * Bursts of an identical notification collapse into the first one with a count.
 */
export function addNotification(partial) {
    const now = Date.now();
    const notif = {
        id: nextId(),
        type: 'error', // 'error' | 'warning' | 'info' | 'success'
        title: 'Notification',
        message: '',
        count: 1,
        ...partial,
        timestamp: now,
    };

    const sig = signatureOf(notif);
    const existing = notifications.find(
        (n) => signatureOf(n) === sig && now - n.timestamp < DEDUPE_WINDOW_MS
    );
    if (existing) {
        notifications = notifications.map((n) =>
            n.id === existing.id ? { ...n, count: n.count + 1, timestamp: now } : n
        );
        emit();
        return existing.id;
    }

    notifications = [notif, ...notifications].slice(0, MAX_NOTIFICATIONS);
    emit();
    return notif.id;
}

export function dismissNotification(id) {
    const next = notifications.filter((n) => n.id !== id);
    if (next.length !== notifications.length) {
        notifications = next;
        emit();
    }
}

export function clearNotifications() {
    if (notifications.length) {
        notifications = [];
        emit();
    }
}

/** Push a non-error notification (info/success/warning). */
export function notify(opts = {}) {
    return addNotification({ type: 'info', ...opts });
}

/**
 * Normalizes any thrown value and surfaces it as an error notification.
 *
 * The error object is tagged (non-enumerably) so the same failure isn't
 * reported twice — e.g. once by the axios interceptor and again when the same
 * rejected promise reaches the global unhandledrejection handler.
 */
export function reportError(error, opts = {}) {
    try {
        if (error && typeof error === 'object') {
            if (error.__qdexReported) return null;
            Object.defineProperty(error, '__qdexReported', {
                value: true,
                enumerable: false,
                configurable: true,
            });
        }
    } catch {
        /* frozen/exotic value — fall through and still report it */
    }

    const normalized = normalizeError(error, opts);
    return addNotification({ ...normalized, type: 'error' });
}

// Exposed for tests / debugging.
export function _resetForTests() {
    notifications = [];
    seq = 0;
    listeners.clear();
}
